import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Button,
  Segment,
  Header,
  Icon,
  Message,
  Divider,
} from "semantic-ui-react";
import {
  SG_ADD_LLM_MODEL_CONFIG,
  SG_EDIT_LLM_MODEL_CONFIG,
  SG_FETCH_LLM_MODEL_CONFIG,
} from "../constants/actions";

const providerOptions = [
  { key: "openai", value: "openai", text: "OpenAI" },
  { key: "anthropic", value: "anthropic", text: "Anthropic" },
  { key: "google", value: "google", text: "Google" },
  { key: "azure", value: "azure", text: "Azure OpenAI" },
  { key: "cohere", value: "cohere", text: "Cohere" },
  { key: "other", value: "other", text: "Other" },
];

export default function ModelConfigForm() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { llm_model_config, llm_model_configs_pending } = useSelector(
    (state) => state.llm_log || {}
  );

  const [formData, setFormData] = useState({
    model_name: "",
    provider: "openai",
    organization: null,
    active: true,
    is_default: false,
    health_status: "ok",
    price_prompt_per_1k_micro_usd: "",
    price_completion_per_1k_micro_usd: "",
    api_token: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && id) {
      dispatch({ type: SG_FETCH_LLM_MODEL_CONFIG, payload: { id } });
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && llm_model_config) {
      setFormData({
        model_name: llm_model_config.model_name || "",
        provider: llm_model_config.provider || "openai",
        organization: llm_model_config.organization || null,
        active: llm_model_config.active ?? true,
        is_default: llm_model_config.is_default || false,
        health_status: llm_model_config.health_status || "ok",
        price_prompt_per_1k_micro_usd: llm_model_config.price_prompt_per_1k_micro_usd || "",
        price_completion_per_1k_micro_usd: llm_model_config.price_completion_per_1k_micro_usd || "",
        api_token: llm_model_config.api_token || "",
        notes: llm_model_config.notes || "",
      });
    }
  }, [llm_model_config, isEdit]);

  const handleChange = (e, { name, value, checked, type }) => {
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.model_name.trim()) {
      newErrors.model_name = "Model name is required";
    }
    if (!formData.provider) {
      newErrors.provider = "Provider is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const payload = {
      ...formData,
      price_prompt_per_1k_micro_usd: formData.price_prompt_per_1k_micro_usd
        ? parseInt(formData.price_prompt_per_1k_micro_usd)
        : 0,
      price_completion_per_1k_micro_usd: formData.price_completion_per_1k_micro_usd
        ? parseInt(formData.price_completion_per_1k_micro_usd)
        : 0,
    };

    if (isEdit) {
      dispatch({
        type: SG_EDIT_LLM_MODEL_CONFIG,
        payload: { ...payload, id },
      });
    } else {
      dispatch({
        type: SG_ADD_LLM_MODEL_CONFIG,
        payload,
      });
    }

    setTimeout(() => {
      navigate("/app/llm-usage/model-configs");
    }, 500);
  };

  return (
    <Segment>
      <Header as="h2">
        <Icon name="cog" />
        <Header.Content>
          {isEdit ? "Edit Model Configuration" : "Add Model Configuration"}
          <Header.Subheader>
            Configure LLM model settings and pricing
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Form loading={llm_model_configs_pending}>
        <Header as="h3" dividing>
          Basic Information
        </Header>

        <Form.Group widths="equal">
          <Form.Input
            required
            label="Model Name"
            name="model_name"
            placeholder="e.g., gpt-4, claude-3-opus"
            value={formData.model_name}
            onChange={handleChange}
            error={errors.model_name}
          />
          <Form.Select
            required
            label="Provider"
            name="provider"
            options={providerOptions}
            value={formData.provider}
            onChange={handleChange}
            error={errors.provider}
          />
        </Form.Group>

        <Form.Input
          label="Organization ID (optional)"
          name="organization"
          type="number"
          placeholder="Leave empty for global config"
          value={formData.organization || ""}
          onChange={handleChange}
        />
        <Message size="small">
          <p>Leave Organization ID empty for global configuration. Set a specific ID for org-specific override.</p>
        </Message>

        <Divider />

        <Header as="h3" dividing>
          Pricing (micro-USD per 1,000 tokens)
        </Header>

        <Message info>
          <p>Prices are stored in micro-USD. 1 USD = 1,000,000 micro-USD</p>
          <p>Example: $0.001 per 1K tokens = 1,000 micro-USD</p>
        </Message>

        <Form.Group widths="equal">
          <Form.Input
            label="Prompt Cost (micro-USD per 1K tokens)"
            name="price_prompt_per_1k_micro_usd"
            type="number"
            placeholder="1000 (= $0.001)"
            value={formData.price_prompt_per_1k_micro_usd}
            onChange={handleChange}
          />
          <Form.Input
            label="Completion Cost (micro-USD per 1K tokens)"
            name="price_completion_per_1k_micro_usd"
            type="number"
            placeholder="3000 (= $0.003)"
            value={formData.price_completion_per_1k_micro_usd}
            onChange={handleChange}
          />
        </Form.Group>

        <Divider />

        <Header as="h3" dividing>
          Configuration & Security
        </Header>

        <Form.Input
          label="Health Status"
          name="health_status"
          placeholder="ok, degraded, down"
          value={formData.health_status}
          onChange={handleChange}
        />

        <Form.TextArea
          label="API Token (Encrypted)"
          name="api_token"
          placeholder="Your API token (stored securely)"
          value={formData.api_token}
          onChange={handleChange}
          rows={2}
        />

        <Form.Group widths="equal">
          <Form.Checkbox
            label="Active"
            name="active"
            checked={formData.active}
            onChange={handleChange}
          />
          <Form.Checkbox
            label="Default for this Provider"
            name="is_default"
            checked={formData.is_default}
            onChange={handleChange}
          />
        </Form.Group>

        <Divider />

        <Form.TextArea
          label="Notes"
          name="notes"
          placeholder="Additional notes about this model configuration..."
          value={formData.notes}
          onChange={handleChange}
          rows={3}
        />

        <Divider />

        <Button.Group>
          <Button
            type="button"
            onClick={() => navigate("/app/llm-usage/model-configs")}
          >
            Cancel
          </Button>
          <Button.Or />
          <Button positive onClick={handleSubmit}>
            {isEdit ? "Update" : "Create"} Configuration
          </Button>
        </Button.Group>
      </Form>
    </Segment>
  );
}